import React, { useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { MESSAGES } from '../../utils/constants';

interface TodoNodeDescriptionProps {
  description: string;
  onUpdateDescription: (description: string) => void;
  isArchived: boolean;
  /** Whether the node is expanded (reveals the full description + the empty-state editor) */
  isExpanded?: boolean;
}

const TodoNodeDescription: React.FC<TodoNodeDescriptionProps> = ({
  description,
  onUpdateDescription,
  isArchived,
  isExpanded = false,
}) => {
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editDescription, setEditDescription] = useState(description);

  const handleDescriptionDoubleClick = useCallback(() => {
    if (!isArchived) {
      setIsEditingDescription(true);
      setEditDescription(description);
    }
  }, [description, isArchived]);

  const handleDescriptionSave = useCallback(() => {
    if (editDescription !== description) {
      onUpdateDescription(editDescription);
    }
    setIsEditingDescription(false);
  }, [editDescription, description, onUpdateDescription]);

  const handleDescriptionKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      handleDescriptionSave();
    } else if (e.key === 'Escape') {
      setIsEditingDescription(false);
      setEditDescription(description);
    }
  }, [handleDescriptionSave, description]);

  // Keep collapsed bare nodes truly compact: when there's no description and the
  // node isn't expanded, render nothing (the "Add description…" affordance shows
  // once the node is expanded).
  if (!isEditingDescription && !isExpanded && (!description || description.trim() === '')) {
    return null;
  }

  return (
    <div className="node-description">
      {isEditingDescription ? (
        <textarea
          value={editDescription}
          onChange={(e) => setEditDescription(e.target.value)}
          onBlur={handleDescriptionSave}
          onKeyDown={handleDescriptionKeyPress}
          className="description-edit"
          placeholder={MESSAGES.DESCRIPTION_PLACEHOLDER}
          autoFocus
        />
      ) : (
        <div
          className={`description-text ${!description ? 'empty' : ''}`}
          onDoubleClick={handleDescriptionDoubleClick}
          title={isArchived ? description || MESSAGES.EMPTY_DESCRIPTION : MESSAGES.EDIT_DESCRIPTION_TOOLTIP}
        >
          {description ? <ReactMarkdown>{description}</ReactMarkdown> : MESSAGES.EMPTY_DESCRIPTION}
        </div>
      )}
    </div>
  );
};

export default TodoNodeDescription;